export type InsightsAnswerSegment =
  | { type: "prose"; text: string }
  | { type: "render"; html: string; complete: boolean }
  | { type: "ledger"; content: string; complete: boolean };

const RENDER_OPEN = "<render>";
const RENDER_CLOSE = "</render>";

const LEDGER_CLOSE_RE = /<\/ledger>/i;
/** After `<ledger`, optional attrs then `>` — never cross into `</ledger>`. */
const LEDGER_ATTRS_THEN_GT = /^(?:\s[^<\/]{0,120})?>/;

type Marker = {
  kind: "render" | "ledger";
  at: number;
  openLen: number;
  close: string;
  closeRe?: RegExp;
};

function findLedgerOpen(text: string, from: number): { at: number; openLen: number } | null {
  const slice = text.slice(from);
  const bare = /<ledger\b/i.exec(slice);
  if (!bare || bare.index === undefined) return null;
  const at = from + bare.index;
  const afterName = text.slice(at + bare[0].length);

  const withGt = LEDGER_ATTRS_THEN_GT.exec(afterName);
  if (withGt) {
    return { at, openLen: bare[0].length + withGt[0].length };
  }

  // Bare `<ledger` (model omitted `>`) — only when a close tag exists later.
  if (LEDGER_CLOSE_RE.test(afterName)) {
    return { at, openLen: bare[0].length };
  }

  // Streaming: `<ledger` / partial attrs with no close yet → live ledger card.
  return { at, openLen: bare[0].length };
}

function nextMarker(text: string, from: number): Marker | null {
  const renderAt = text.indexOf(RENDER_OPEN, from);
  const ledger = findLedgerOpen(text, from);
  const ledgerAt = ledger?.at ?? -1;

  if (renderAt === -1 && ledgerAt === -1) return null;
  if (ledgerAt === -1 || (renderAt !== -1 && renderAt < ledgerAt)) {
    return { kind: "render", at: renderAt, openLen: RENDER_OPEN.length, close: RENDER_CLOSE };
  }
  return {
    kind: "ledger",
    at: ledgerAt,
    openLen: ledger!.openLen,
    close: "</ledger>",
    closeRe: LEDGER_CLOSE_RE,
  };
}

function findClose(text: string, contentStart: number, marker: Marker): number {
  if (marker.closeRe) {
    const slice = text.slice(contentStart);
    const m = marker.closeRe.exec(slice);
    if (!m || m.index === undefined) return -1;
    return contentStart + m.index;
  }
  return text.indexOf(marker.close, contentStart);
}

function closeLen(marker: Marker, text: string, closeAt: number): number {
  if (marker.closeRe) {
    const m = marker.closeRe.exec(text.slice(closeAt));
    return m ? m[0].length : marker.close.length;
  }
  return marker.close.length;
}

/** Strip residual raw ledger/render tag fragments from prose (defense in depth). */
export function stripResidualBlockTags(text: string): string {
  return text
    .replace(/<\/ledger>/gi, "")
    .replace(/<\/render>/gi, "")
    .replace(/<ledger\b[^>]*>/gi, "")
    .replace(/<render\b[^>]*>/gi, "")
    .replace(/<ledger\b/gi, "")
    .replace(/<render\b/gi, "");
}

/**
 * Split assistant text into prose + <render> + <ledger> segments.
 * Incomplete tags: render becomes a live (incomplete) segment — never raw tag prose;
 * ledger becomes a live card. Tolerates malformed ledger opens.
 */
export function parseInsightsAnswerSegments(text: string): InsightsAnswerSegment[] {
  const segments: InsightsAnswerSegment[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    const marker = nextMarker(text, cursor);
    if (!marker) {
      const rest = stripResidualBlockTags(text.slice(cursor));
      if (rest) segments.push({ type: "prose", text: rest });
      break;
    }

    if (marker.at > cursor) {
      const before = stripResidualBlockTags(text.slice(cursor, marker.at));
      if (before) segments.push({ type: "prose", text: before });
    }

    const contentStart = marker.at + marker.openLen;
    const closeAt = findClose(text, contentStart, marker);
    if (closeAt === -1) {
      const tail = text.slice(contentStart);
      if (marker.kind === "ledger") {
        segments.push({ type: "ledger", content: tail, complete: false });
      } else {
        // Streaming visual — keep as render (not prose) so UI can show a shell/skeleton.
        segments.push({ type: "render", html: tail, complete: false });
      }
      break;
    }

    const body = text.slice(contentStart, closeAt);
    if (marker.kind === "ledger") {
      segments.push({ type: "ledger", content: body, complete: true });
    } else {
      segments.push({ type: "render", html: body, complete: true });
    }
    cursor = closeAt + closeLen(marker, text, closeAt);
  }

  return segments;
}

/** Reject full documents / CDN bootstraps that violate the design-system contract. */
export function sanitizeRenderFragment(html: string): string {
  const trimmed = html.trim();
  if (/<!DOCTYPE|<html[\s>]|<head[\s>]|<body[\s>]/i.test(trimmed)) {
    return `<div style="color:var(--color-text-danger);padding:1rem">Invalid render fragment (full document not allowed).</div>`;
  }
  if (/<link\s|<script[^>]+src=/i.test(trimmed)) {
    return `<div style="color:var(--color-text-danger);padding:1rem">Invalid render fragment (external scripts/links not allowed).</div>`;
  }
  return trimmed;
}
